import BodyHelper from "../../Utils/BodyHelper";
import Helper from "../../Utils/Helper";
import Validation from "../../Utils/Validation";
import { useDisclosure } from "@chakra-ui/react";
import { useCallback, useEffect, useRef, useState } from "react";

import { useLocation } from "react-router-dom";
import { useChatDataSource } from "../../Data/DataSourceContext";
import { withMutationDefaults } from "../../Data/DataSourceHelpers";
import { useChatState } from "../../StateContext";
import { useChatApp } from "../../AppContext";
import {
  ROOM_LIMIT,
  createInitialRoomState,
  fallbackRoomsQuery,
  resolveRoomsPayload,
  resolveUnreadPayload,
  sortRoomsByActivity,
} from "./helpers";
export default function useRooms({ Socket, Lang = {} }) {
  const InitialState = createInitialRoomState();
  const { selectedRoom, setSelectedRoom, resetChatData, filter, setFilter } =
    useChatState();
  let SelectedRoomId = selectedRoom?.RoomId || null;
  const { Page, Op, SearchBy, SearchValue, To } = filter;
  const { CurrentUser } = useChatApp();
  const UserId = CurrentUser?.Id;
  const DataSource = useChatDataSource();
  const RoomsDataSource = DataSource?.Rooms || {};
  const useRoomsQueryFromProvider =
    RoomsDataSource.UseRoomsQuery || fallbackRoomsQuery;
  const CreateMutation = withMutationDefaults(RoomsDataSource.Create);
  const DeleteMutation = withMutationDefaults(RoomsDataSource.Delete);
  const FavoriteMutation = withMutationDefaults(RoomsDataSource.ToggleFavorite);
  const UpdateMutation = withMutationDefaults(RoomsDataSource.Update);
  const FetchRoomsMutation = withMutationDefaults(RoomsDataSource.List);
  const CreateItem = CreateMutation.run;
  const DeleteItem = DeleteMutation.run;
  const FavoriteItem = FavoriteMutation.run;
  const UpdateItem = UpdateMutation.run;
  const FetchRooms = FetchRoomsMutation.run;
  const {
    isLoading: IsCreating,
    isError: isErrorCreate,
    error: errorCreate,
  } = CreateMutation.state;
  const {
    isLoading: IsDeleting,
    isError: isErrorDelete,
    error: errorDelete,
  } = DeleteMutation.state;
  const {
    isLoading: IsFavoring,
    isError: isErrorFavorite,
    error: errorFavorite,
  } = FavoriteMutation.state;
  const {
    isLoading: IsUpdating,
    isError: isErrorUpdate,
    error: errorUpdate,
  } = UpdateMutation.state;
  const { isLoading: IsManuallyFetching } = FetchRoomsMutation.state;
  const [State, SetState] = useState(InitialState);
  const [Rooms, SetRooms] = useState([]);
  const RoomsRef = useRef(Rooms);
  const [Total, SetTotal] = useState(0);
  const [HasSyncedTotal, SetHasSyncedTotal] = useState(false);
  const [Unread, SetUnread] = useState({});
  const clearedUnreadRef = useRef({});
  const visibleRoomIdsRef = useRef(new Set());
  const pendingMissingIdsRef = useRef(new Set());
  const missingFetchInProgressRef = useRef(false);
  const [MissingQueueVersion, SetMissingQueueVersion] = useState(0);
  const { isOpen: IsModelOpen, onToggle: OnModelToggle } = useDisclosure();

  const ApplyFavoriteLocally = useCallback(
    (roomId, isFavorite) => {
      if (!roomId && roomId !== 0) return;
      SetRooms((prev = []) =>
        prev.map((room) =>
          room.RoomId == roomId ? { ...room, RoomIsFavorite: isFavorite } : room
        )
      );
      if (roomId == SelectedRoomId && selectedRoom) {
        setSelectedRoom({
          ...selectedRoom,
          RoomIsFavorite: isFavorite,
        });
      }
    },
    [SelectedRoomId, selectedRoom, setSelectedRoom]
  );
  const SortRooms = useCallback((RoomsData = []) => {
    return sortRoomsByActivity(RoomsData);
  }, []);
  const ResolveUnreadPayload = useCallback((payload) => {
    return resolveUnreadPayload(payload);
  }, []);
  const ResolveRoomsPayload = useCallback((payload) => {
    return resolveRoomsPayload(payload);
  }, []);
  const QueueMissingRoom = useCallback((roomId) => {
    const numericId = Number(roomId);
    if (!numericId) return;
    if (visibleRoomIdsRef.current.has(numericId)) return;
    if (pendingMissingIdsRef.current.has(numericId)) return;
    pendingMissingIdsRef.current.add(numericId);
    SetMissingQueueVersion((prev) => prev + 1);
  }, []);
  useEffect(() => {
    const ids = new Set();
    if (Array.isArray(Rooms)) {
      Rooms.forEach((room) => {
        if (room?.RoomId) {
          ids.add(Number(room.RoomId));
        }
      });
    }
    visibleRoomIdsRef.current = ids;
    RoomsRef.current = Rooms;
  }, [Rooms]);
  const {
    data,
    isLoading: IsLoading,
    isError,
    error: RoomsError,
    refetch,
    isUninitialized,
  } = useRoomsQueryFromProvider(
    {
      Sort: "DESC",
      OrderBy: "UnreadCount",
      Page: Page,
      Limit: ROOM_LIMIT,
      Op,
      To,
      SearchBy,
      Search: SearchValue,
    },
    {
      skip: !Socket || (SearchBy && !String(SearchBy).startsWith("Room")),
      refetchOnMountOrArgChange: true,
    }
  );
  const AnnotateRoom = useCallback(
    (room) => {
      if (!room) return room;
      const entry = Unread[room.RoomId];
      const lastMessageAt =
        room.LastMessage?.MessageCreatedAt ||
        room.LastMessageAt ||
        room.RoomUpdatedAt ||
        room.RoomCreatedAt ||
        null;
      return {
        ...room,
        LastMessageAt: lastMessageAt,
        UnreadCount: entry?.Count ?? room.UnreadCount ?? 0,
      };
    },
    [Unread]
  );
  const ApplyUnreadUpdates = useCallback(
    (payload) => {
      const entriesSource = ResolveUnreadPayload(payload);
      if (!entriesSource) return;
      const entries = Array.isArray(entriesSource)
        ? entriesSource
        : [entriesSource];
      const normalized = entries
        .map((entry) => {
          const RoomId = Number(entry?.RoomId || entry?.roomId);
          if (!RoomId) return null;
          const CountValue =
            entry?.UnreadCount ??
            entry?.Count ??
            entry?.unreadCount ??
            entry?.count ??
            0;
          const Count = Number.isFinite(Number(CountValue))
            ? Number(CountValue)
            : 0;
          const payloadSenderId = Number(
            entry?.SenderId ||
              entry?.Sender?.Id ||
              entry?.Sender?.UserId ||
              entry?.UserId
          );
          const normalizedCount =
            payloadSenderId && UserId && Number(UserId) === payloadSenderId
              ? 0
              : Count;
          const lastMessageAtValue =
            entry?.LastMessageAt ||
            entry?.lastMessageAt ||
            entry?.LastMessage?.MessageCreatedAt ||
            entry?.RoomLastMessageAt ||
            null;
          const clearedAt = clearedUnreadRef.current[RoomId];
          let effectiveCount = normalizedCount;
          if (clearedAt) {
            const entryTs = lastMessageAtValue
              ? new Date(lastMessageAtValue).getTime()
              : null;
            if (!entryTs || clearedAt >= entryTs) {
              effectiveCount = 0;
            }
          }
          return {
            RoomId,
            Count: effectiveCount < 0 ? 0 : effectiveCount,
            LastMessageAt: lastMessageAtValue,
          };
        })
        .filter(Boolean);
      if (!normalized.length) return;
      normalized.forEach(({ RoomId }) => {
        if (!visibleRoomIdsRef.current.has(RoomId)) {
          QueueMissingRoom(RoomId);
        }
      });
      const updatesByRoom = normalized.reduce((acc, curr) => {
        acc[curr.RoomId] = curr;
        return acc;
      }, {});
      SetUnread((prev) => {
        const next = { ...prev };
        normalized.forEach(({ RoomId, Count, LastMessageAt }) => {
          const previousEntry = prev[RoomId];
          next[RoomId] = {
            Count,
            LastMessageAt:
              LastMessageAt ?? previousEntry?.LastMessageAt ?? null,
          };
        });
        return next;
      });
      SetRooms((prev) => {
        if (!Array.isArray(prev) || prev.length === 0) return prev;
        let changed = false;
        const updated = prev.map((room) => {
          const update = updatesByRoom[room.RoomId];
          if (!update) return room;
          const nextLastMessageAt =
            update.LastMessageAt ?? room.LastMessageAt ?? null;
          if (
            room.UnreadCount === update.Count &&
            (nextLastMessageAt || null) === (room.LastMessageAt || null)
          ) {
            return room;
          }
          changed = true;
          return {
            ...room,
            UnreadCount: update.Count,
            LastMessageAt: nextLastMessageAt,
          };
        });
        return changed ? SortRooms(updated) : prev;
      });
    },
    [QueueMissingRoom, ResolveUnreadPayload, SortRooms, UserId]
  );
  const RequestUnreadSummary = useCallback(() => {
    if (!Socket) return;
    Socket.emit("Room", { command: "UnreadSummary" }, (payload) => {
      if (!payload || payload?.error) return;
      const summary = ResolveUnreadPayload(payload);
      if (summary) {
        ApplyUnreadUpdates(summary);
      }
    });
  }, [ApplyUnreadUpdates, ResolveUnreadPayload, Socket]);
  const HandleData = useCallback(
    (data, CurrentPage) => {
      if (!data) return;
      const results = Array.isArray(data?.results) ? data.results : [];
      const metaTotal =
        typeof data?.meta?.Total === "number" ? data.meta.Total : null;
      if (metaTotal !== null) {
        SetTotal(metaTotal);
        SetHasSyncedTotal(true);
      } else if (!HasSyncedTotal && CurrentPage === 1) {
        SetTotal(results.length);
      }
      if (results.length === 0) {
        if (CurrentPage === 1) {
          SetRooms([]);
          SetUnread({});
        }
        return;
      }
      const withUnread = results.map((room) => ({
        ...AnnotateRoom(room),
        __injected: false,
      }));
      SetRooms((prev) => {
        let next = prev || [];
        if (CurrentPage === 1) {
          const officialIds = new Set(
            withUnread.map((room) => Number(room.RoomId))
          );
          const injectedCarry = Array.isArray(prev)
            ? prev.filter(
                (room) =>
                  room?.__injected && !officialIds.has(Number(room?.RoomId))
              )
            : [];
          next = SortRooms([...injectedCarry, ...withUnread]);
        } else if (!prev?.length) {
          next = SortRooms(withUnread);
        } else {
          const newItems = withUnread.filter(
            (newItem) =>
              !prev.some((oldItem) => oldItem.RoomId === newItem.RoomId)
          );
          next = SortRooms([...prev, ...newItems]);
        }
        return next;
      });
      const updates = {};
      withUnread.forEach((room) => {
        updates[room.RoomId] = {
          Count: room.UnreadCount || 0,
          LastMessageAt: room.LastMessageAt || null,
        };
      });
      if (Object.keys(updates).length > 0) {
        SetUnread((prev) => ({
          ...prev,
          ...updates,
        }));
      }
    },
    [AnnotateRoom, HasSyncedTotal, SortRooms]
  );
  const handleDataRef = useRef(HandleData);
  useEffect(() => {
    handleDataRef.current = HandleData;
  }, [HandleData]);
  useEffect(() => {
    const payload = ResolveRoomsPayload(data);
    if (payload) {
      handleDataRef.current?.(payload, Page);
    }
  }, [ResolveRoomsPayload, data, Page]);
  useEffect(() => {
    if (
      MissingQueueVersion === 0 ||
      missingFetchInProgressRef.current ||
      !pendingMissingIdsRef.current.size
    ) {
      return;
    }
    let mounted = true;
    const fetchMissingRooms = async () => {
      if (missingFetchInProgressRef.current) return;
      missingFetchInProgressRef.current = true;
      try {
        const batchSize = 4;
        const ids = Array.from(pendingMissingIdsRef.current).slice(
          0,
          batchSize
        );
        ids.forEach((id) => pendingMissingIdsRef.current.delete(id));
        const fetchedRooms = [];
        for (const id of ids) {
          try {
            const response = await FetchRooms({
              Sort: "DESC",
              OrderBy: "UnreadCount",
              Page: 1,
              Limit: 1,
              Op: "eq",
              SearchBy: "RoomId",
              Search: id,
            });
            const payload = ResolveRoomsPayload(
              response?.data || response
            );
            const room =
              payload?.results?.find(
                (item) => Number(item.RoomId) === Number(id)
              ) || null;
            if (room) {
              fetchedRooms.push(room);
            }
          } catch {
            // ignore fetch failure for this id
          }
        }
        if (mounted && fetchedRooms.length) {
          const annotated = fetchedRooms.map((room) => ({
            ...AnnotateRoom(room),
            __injected: true,
          }));
          SetRooms((prev) => {
            const existingIds = new Set(
              prev.map((room) => Number(room.RoomId))
            );
            const merged = [
              ...annotated.filter(
                (room) => !existingIds.has(Number(room.RoomId))
              ),
              ...prev,
            ];
            return SortRooms(merged);
          });
          const updates = {};
          annotated.forEach((room) => {
            if (!room?.RoomId) return;
            updates[room.RoomId] = {
              Count: room.UnreadCount || 0,
              LastMessageAt: room.LastMessageAt || null,
            };
          });
          if (Object.keys(updates).length > 0) {
            SetUnread((prev) => ({
              ...prev,
              ...updates,
            }));
          }
        }
      } finally {
        missingFetchInProgressRef.current = false;
        if (pendingMissingIdsRef.current.size) {
          SetMissingQueueVersion((prev) => prev + 1);
        }
      }
    };
    fetchMissingRooms();
    return () => {
      mounted = false;
    };
  }, [
    AnnotateRoom,
    FetchRooms,
    MissingQueueVersion,
    ResolveRoomsPayload,
    SortRooms,
  ]);
  useEffect(() => {
    if (!HasSyncedTotal || isUninitialized) return;
    const maxPage = Total > 0 ? Math.max(1, Math.ceil(Total / ROOM_LIMIT)) : 1;
    if (Page > maxPage) {
      setFilter({ Page: maxPage });
    }
  }, [HasSyncedTotal, Page, Total, isUninitialized, setFilter]);
  useEffect(() => {
    if (!Socket) return;
    const OnConnect = () => RequestUnreadSummary();
    if (Socket.connected) {
      RequestUnreadSummary();
    }
    Socket.on("connect", OnConnect);
    return () => {
      Socket.off("connect", OnConnect);
    };
  }, [Socket, RequestUnreadSummary]);
  useEffect(() => {
    Helper.HandleError(isError, RoomsError);
    Helper.HandleError(isErrorCreate, errorCreate);
    Helper.HandleError(isErrorUpdate, errorUpdate);
    Helper.HandleError(isErrorDelete, errorDelete);
    Helper.HandleError(isErrorFavorite, errorFavorite);
  }, [
    isErrorUpdate,
    isErrorCreate,
    isErrorDelete,
    isError,
    isErrorFavorite,
    RoomsError,
    errorCreate,
    errorDelete,
    errorFavorite,
    errorUpdate,
  ]);
  const MarkRoomAsRead = useCallback(
    (RoomId, lastMessageAt) => {
      if (!RoomId) return;
      if (lastMessageAt) {
        const ts = new Date(lastMessageAt).getTime();
        if (Number.isFinite(ts)) {
          clearedUnreadRef.current[RoomId] = ts;
        } else {
          clearedUnreadRef.current[RoomId] = Date.now();
        }
      } else {
        clearedUnreadRef.current[RoomId] = Date.now();
      }
      ApplyUnreadUpdates({ RoomId, UnreadCount: 0 });
    },
    [ApplyUnreadUpdates]
  );
  const OnSelectRoom = useCallback(
    (item) => {
      if (!item?.RoomId || item.RoomId === SelectedRoomId) return;
      setSelectedRoom(item);
      const lastAt =
        item?.LastMessageAt ||
        item?.LastMessage?.MessageCreatedAt ||
        new Date().toISOString();
      MarkRoomAsRead(item.RoomId, lastAt);
    },
    [MarkRoomAsRead, SelectedRoomId, setSelectedRoom]
  );
  const { search } = useLocation();
  useEffect(() => {
    const Search = new URLSearchParams(search);
    const RoomId = Search.get("r");
    if (!RoomId || !Rooms?.length) return;
    const Room = Rooms.find((item) => String(item.RoomId) === String(RoomId));
    if (Room) {
      OnSelectRoom(Room);
      window.history.pushState({}, "", window.location.pathname);
    }
  }, [search, Rooms, OnSelectRoom]);
  const PreviousRoomRef = useRef(null);
  // Track SelectedRoomId via ref so the reconnect handler can read it
  // without being in its dependency array (avoids duplicate Join on every room switch).
  const selectedRoomForReconnectRef = useRef(SelectedRoomId);
  useEffect(() => {
    selectedRoomForReconnectRef.current = SelectedRoomId;
  }, [SelectedRoomId]);
  useEffect(() => {
    if (!Socket) return;
    const PrevRoom = PreviousRoomRef.current;
    if (PrevRoom && PrevRoom !== SelectedRoomId) {
      Socket.emit("Room", { command: "Leave", query: { RoomId: PrevRoom } });
    }
    if (SelectedRoomId) {
      Socket.emit("Room", {
        command: "Join",
        query: { RoomId: SelectedRoomId },
      });
    }
    PreviousRoomRef.current = SelectedRoomId;
  }, [Socket, SelectedRoomId]);
  useEffect(() => {
    if (!Socket) return;
    const handleConnect = () => {
      const currentRoom = selectedRoomForReconnectRef.current || PreviousRoomRef.current;
      if (currentRoom) {
        Socket.emit("Room", {
          command: "Join",
          query: { RoomId: currentRoom },
        });
      }
    };
    Socket.on("connect", handleConnect);
    return () => {
      Socket.off("connect", handleConnect);
    };
  }, [Socket]);
  const OnCreate = () => {
    SetState({
      ...InitialState,
      Tab: "Create",
    });
    OnModelToggle();
  };
  const OnClose = () => {
    SetState(InitialState);
    if (IsModelOpen) {
      OnModelToggle();
    }
  };
  const OnSubmit = async (Form) => {
    if (["Create", "Edit"].includes(Form.Tab)) {
      const Validate = Validation.Room(Form);
      if (Validate.length > 0) {
        return Helper.Notify("warn", Lang?.Validation?.[Validate[0]] || Validate[0]);
      }
    }
    const Body = BodyHelper.Room(Form);
    const Func = {
      Create: CreateItem,
      Edit: UpdateItem,
      Delete: DeleteItem,
      Favorite: FavoriteItem,
      UnFavorite: FavoriteItem,
    };
    if (!Func[Form?.Tab]) return;
    const isFavoriteToggle = ["Favorite", "UnFavorite"].includes(Form.Tab);
    const optimisticFavorite = Form.Tab === "Favorite";
    let favoriteRoomId = null;
    let previousFavorite = null;
    if (isFavoriteToggle) {
      favoriteRoomId =
        Form?.RoomId || Form?.Room?.RoomId || State?.RoomId || null;
      if (favoriteRoomId) {
        const existing = Rooms.find(
          (room) => room.RoomId == Number(favoriteRoomId)
        );
        previousFavorite = existing?.RoomIsFavorite ?? false;
        ApplyFavoriteLocally(Number(favoriteRoomId), optimisticFavorite);
      }
    }
    try {
      const { data } = await Func[Form?.Tab](Body);
      if (data && !data?.error) {
        if (isFavoriteToggle) {
          const { RoomId, Favorite } = data?.data || {};
          if (RoomId != null) {
            ApplyFavoriteLocally(RoomId, Favorite);
          }
        }
        OnModelToggle();
      } else if (isFavoriteToggle && favoriteRoomId !== null) {
        ApplyFavoriteLocally(Number(favoriteRoomId), previousFavorite);
      }
    } catch (error) {
      if (isFavoriteToggle && favoriteRoomId !== null) {
        ApplyFavoriteLocally(Number(favoriteRoomId), previousFavorite);
      }
      Helper.HandleError(true, error);
    }
  };
  const OnChange = (Name, Value) => {
    if (Value === "Submit") {
      OnSubmit(Name);
      return;
    }
    if (typeof Name == "object") {
      SetState((prev) => ({
        ...prev,
        ...Name,
      }));
      return;
    }
    if (Name == "Customers") {
      if (Value) {
        Value = [Value];
      }
    }
    SetState((prev) => ({
      ...prev,
      [Name]: Value,
    }));
  };
  const OnSocketCreate = useCallback(
    (data) => {
      const { RoomId } = data || {};
      if (!RoomId) return;
      const IsSelected = SelectedRoomId == RoomId;
      const NormalizedRoom = AnnotateRoom(data);
      SetRooms((prev) => {
        const AlreadyExists = prev.some((room) => room.RoomId === RoomId);
        if (AlreadyExists)
          return SortRooms(
            prev?.map((item) => (item.RoomId == RoomId ? NormalizedRoom : item))
          );
        return SortRooms([NormalizedRoom, ...prev]);
      });
      SetUnread((prev) => {
        if (prev[RoomId]) return prev;
        return {
          ...prev,
          [RoomId]: {
            Count: NormalizedRoom?.UnreadCount || 0,
            LastMessageAt: NormalizedRoom?.LastMessageAt || null,
          },
        };
      });
      if (IsSelected) {
        setSelectedRoom((prev) => ({
          ...(prev || {}),
          ...NormalizedRoom,
        }));
      }
    },
    [SelectedRoomId, AnnotateRoom, SortRooms, setSelectedRoom]
  );
  const OnSocketUpdate = useCallback(
    (data) => {
      const { RoomId } = data || {};
      if (!RoomId) return;
      const IsSelected = SelectedRoomId == RoomId;
      const NormalizedRoom = AnnotateRoom(data);
      SetRooms((prev) => {
        const FindIsExist = prev.find((item) => item.RoomId == RoomId);
        if (!FindIsExist) {
          QueueMissingRoom(RoomId);
          return prev;
        }
        return SortRooms(
          prev.map((item) => (item.RoomId == RoomId ? NormalizedRoom : item))
        );
      });
      if (IsSelected) {
        setSelectedRoom((prev) => ({
          ...(prev || {}),
          ...NormalizedRoom,
        }));
      }
    },
    [SelectedRoomId, AnnotateRoom, SortRooms, QueueMissingRoom, setSelectedRoom]
  );
  const OnSocketDelete = useCallback(
    (data) => {
      const { RoomId } = data || {};
      if (!RoomId) return;
      if (SelectedRoomId == RoomId) {
        resetChatData();
      }
      SetRooms((prev) => prev.filter((item) => item.RoomId != RoomId));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [SelectedRoomId]
  );
  const OnUnreadEvent = useCallback(
    (payload) => {
      const parsed = ResolveUnreadPayload(payload);
      if (parsed) {
        ApplyUnreadUpdates(parsed);
      }
    },
    [ApplyUnreadUpdates, ResolveUnreadPayload]
  );
  const HandlePresence = useCallback(
    (data) => {
      const { command, data: PreData } = data || {};
      if (command !== "Presence" || !PreData) return;

      const { MemberId, Type, Presence } = PreData || {};
      if (Type === "User" && MemberId == CurrentUser?.Id) return;
      let updatedRoom = null;
      let UpdatedCurr = false;
      let UpdatedCurrData = null;
      SetRooms((prevRooms) => {
        if (!Array.isArray(prevRooms)) return prevRooms;
        const newRooms = prevRooms.map((room) => {
          const { RoomCreator, RoomUsers = [], RoomCustomers = [] } = room;
          let updated = false;
          if (Type == "User" && RoomCreator?.UserId == MemberId) {
            updated = true;
            updatedRoom = {
              ...room,
              RoomCreator: {
                ...RoomCreator,
                UserOnlineStatus: Presence,
              },
            };
          }
          if (
            !updated &&
            Type === "User" &&
            RoomUsers.some((u) => u.UserId === MemberId)
          ) {
            updated = true;
            updatedRoom = {
              ...room,
              RoomUsers: RoomUsers.map((u) =>
                u.UserId == MemberId ? { ...u, UserOnlineStatus: Presence } : u
              ),
            };
          }
          if (
            !updated &&
            Type !== "User" &&
            RoomCustomers.some((c) => c.CustomerId === MemberId)
          ) {
            updated = true;
            updatedRoom = {
              ...room,
              RoomCustomers: RoomCustomers.map((c) =>
                c.CustomerId == MemberId
                  ? { ...c, CustomerOnlineStatus: Presence }
                  : c
              ),
            };
          }
          if (updated && room.RoomId == SelectedRoomId) {
            UpdatedCurr = true;
            UpdatedCurrData = { ...updatedRoom };
          }
          return updated ? updatedRoom : room;
        });
        return newRooms;
      });
      if (UpdatedCurr) {
        setSelectedRoom((prev) => ({
          ...(prev || {}),
          ...UpdatedCurrData,
        }));
      }
    },
    [CurrentUser?.Id, SelectedRoomId, setSelectedRoom]
  );
  const presenceHandlerRef = useRef(HandlePresence);
  useEffect(() => {
    presenceHandlerRef.current = HandlePresence;
  }, [HandlePresence]);
  useEffect(() => {
    if (!Socket) return;
    const handler = (payload) => presenceHandlerRef.current?.(payload);
    Socket.on("Root", handler);
    return () => {
      Socket.off("Root", handler);
    };
  }, [Socket]);
  const OnLastMsg = useCallback(
    (data) => {
      const { RoomId: MessageRoomId, Content, CreatedAt } = data || {};
      if (!MessageRoomId || !Content) return;
      const senderId = Number(
        data?.Sender?.Id ||
          data?.Sender?.UserId ||
          data?.SenderId ||
          data?.UserId ||
          data?.MessageSenderId
      );
      const isOwnSender =
        senderId && UserId ? Number(UserId) === senderId : false;
      const CurrentRooms = RoomsRef.current;
      const FindRoom = CurrentRooms?.find((item) => item.RoomId == MessageRoomId);
      if (!FindRoom) {
        if (!isOwnSender) {
          QueueMissingRoom(MessageRoomId);
        }
        return;
      }
      const Contents = {
        ATTACHMENT: Lang?.CHAT?.ATTACHMENT || "Attachment",
        DELETED_MSG:
          Lang?.CHAT?.DELETED_MSG || "This message has been deleted.",
      };
      let nextUnreadCount = null;
      if (isOwnSender) {
        const ts = CreatedAt ? new Date(CreatedAt).getTime() : Date.now();
        clearedUnreadRef.current[MessageRoomId] = ts;
      }
      SetRooms((prev) => {
        const mapped = prev.map((item) => {
          if (item.RoomId == MessageRoomId) {
            const isActive = item.RoomId == SelectedRoomId;
            const nextCount =
              isActive || isOwnSender ? 0 : (item.UnreadCount || 0) + 1;
            if (!isActive && !isOwnSender) {
              nextUnreadCount = nextCount;
            }
            return {
              ...item,
              LastMessage: {
                MessageContent: Contents[Content] || Content,
                MessageCreatedAt: CreatedAt,
              },
              LastMessageAt: CreatedAt || item.LastMessageAt,
              UnreadCount: nextCount,
            };
          }
          return item;
        });
        return SortRooms(mapped);
      });
      if (nextUnreadCount !== null) {
        SetUnread((prev) => ({
          ...prev,
          [MessageRoomId]: {
            Count: nextUnreadCount,
            LastMessageAt:
              CreatedAt || prev[MessageRoomId]?.LastMessageAt || null,
          },
        }));
      }
    },
    [
      Lang?.CHAT?.ATTACHMENT,
      Lang?.CHAT?.DELETED_MSG,
      SelectedRoomId,
      SortRooms,
      QueueMissingRoom,
      UserId,
    ]
  );
  const roomHandlersRef = useRef({
    OnSocketDelete,
    OnSocketUpdate,
    OnSocketCreate,
    OnLastMsg,
    OnUnreadEvent,
  });
  useEffect(() => {
    roomHandlersRef.current = {
      OnSocketDelete,
      OnSocketUpdate,
      OnSocketCreate,
      OnLastMsg,
      OnUnreadEvent,
    };
  }, [
    OnSocketDelete,
    OnSocketUpdate,
    OnSocketCreate,
    OnLastMsg,
    OnUnreadEvent,
  ]);
  useEffect(() => {
    if (!Socket) return;
    const handler = (data) => {
      const {
        OnSocketDelete,
        OnSocketUpdate,
        OnSocketCreate,
        OnLastMsg,
        OnUnreadEvent,
      } = roomHandlersRef.current;
      if (["Delete"].includes(data?.command)) {
        OnSocketDelete?.(data?.data);
      } else if (data?.command === "Update") {
        OnSocketUpdate?.(data?.data);
      } else if (["Join", "Create", "GetOrCreate"].includes(data?.command)) {
        OnSocketCreate?.(data?.data);
      } else if (data?.command === "LastMsg") {
        OnLastMsg?.(data?.data);
      } else if (
        data?.command === "UnreadUpdated" ||
        data?.command === "UnreadSummary"
      ) {
        OnUnreadEvent?.(data?.data);
      }
    };
    Socket.on("Room", handler);
    return () => {
      Socket.off("Room", handler);
    };
  }, [Socket]);
  const OnRefetch = useCallback(async () => {
    if (
      isUninitialized ||
      IsManuallyFetching ||
      !Socket ||
      (SearchBy && !String(SearchBy).startsWith("Room"))
    ) {
      return;
    }
    const requestOrderBy = "UnreadCount";
    const requestSort = "DESC";
    try {
      const hasMultiplePages = Total > ROOM_LIMIT;
      const targetPage = hasMultiplePages ? Page : 1;
      const response = await FetchRooms({
        Sort: requestSort,
        OrderBy: requestOrderBy,
        Page: targetPage,
        Limit: ROOM_LIMIT,
        Op,
        To,
        SearchBy,
        Search: SearchValue,
      });
      const payload = ResolveRoomsPayload(response?.data || response);
      if (payload) {
        HandleData(payload, targetPage);
        const serverMeta = payload?.meta || {};
        const serverPage =
          Number(serverMeta?.Page) > 0 ? Number(serverMeta.Page) : targetPage;
        const totalFromMeta =
          typeof serverMeta?.Total === "number" ? serverMeta.Total : null;
        if (totalFromMeta !== null) {
          const maxPage = Math.max(1, Math.ceil(totalFromMeta / ROOM_LIMIT));
          const desiredPage = Math.min(Math.max(serverPage, 1), maxPage);
          if (desiredPage !== Page) {
            setFilter({ Page: desiredPage });
          }
        }

        return;
      }
      const fallbackResult = await refetch();
      const fallbackPayload =
        ResolveRoomsPayload(fallbackResult?.data) ||
        ResolveRoomsPayload(fallbackResult);
      if (fallbackPayload) {
        HandleData(fallbackPayload, Page);
      }
    } catch (error) {
      Helper.HandleError(true, error);
    }
  }, [
    FetchRooms,
    HandleData,
    IsManuallyFetching,
    Page,
    ResolveRoomsPayload,
    Total,
    Socket,
    isUninitialized,
    SearchBy,
    SearchValue,
    Op,
    To,
    refetch,
    setFilter,
  ]);
  return {
    OnSelectRoom,
    OnModelToggle,
    IsModelOpen,
    IsCreating,
    IsUpdating,
    IsDeleting,
    IsFavoring,
    IsLoading: IsLoading || IsManuallyFetching,
    OnRefetch,
    OnSubmit,
    OnChange,
    OnCreate,
    OnClose,
    SetPage: (p) => setFilter({ Page: p }),
    State,
    Rooms,
    Total,
    Page,
  };
}
